import { DragDropModule } from '@angular/cdk/drag-drop';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { EditorModule } from '@progress/kendo-angular-editor';
import { BodyModule, GridModule, RowFilterModule, SharedModule } from '@progress/kendo-angular-grid';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import '@progress/kendo-angular-intl/locales/da/all';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/fr/all';
import '@progress/kendo-angular-intl/locales/it/all';
import { MessageService } from '@progress/kendo-angular-l10n';
import { LayoutModule, TabStripModule } from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { PopupModule } from '@progress/kendo-angular-popup';
import { SchedulerModule } from '@progress/kendo-angular-scheduler';
import { SortableModule } from '@progress/kendo-angular-sortable';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { FileSelectModule, UploadModule } from '@progress/kendo-angular-upload';
import { GridsterModule } from 'angular-gridster2';
import { CookieService } from 'ngx-cookie-service';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MomentModule } from 'ngx-moment';
import { ToastContainerModule, ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { ApiModule, BASE_PATH } from '../generated/swagger';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AgentStatusComponent } from './components/agent-information/agent-status/agent-status.component';
import { AcdSelectionFormComponent } from './components/authentication-process/acd-selection-form/acd-selection-form.component';
import { AuthenticationLoadingComponent } from './components/authentication-process/authentication-loading/authentication-loading.component';
import { AuthenticationProcessComponent } from './components/authentication-process/authentication-process.component';
import { AuthorizationFormComponent } from './components/authentication-process/authorization-form/authorization-form.component';
import { AddBrickDialogComponent } from './components/brick-management/add-brick-dialog/add-brick-dialog.component';
import { BrickDefinitionItemComponent } from './components/brick-management/add-brick-dialog/brick-definition-item/brick-definition-item.component';
import { AgentDetailsBrickComponent } from './components/bricks/calling/agent-details-brick/agent-details-brick.component';
import { AgentDetailsSettingsComponent } from './components/bricks/calling/agent-details-brick/agent-details-settings/agent-details-settings.component';
import { CallHistoryForAgentComponent } from './components/bricks/calling/agent-details-brick/call-history-for-agent/call-history-for-agent.component';
import { GroupNamesForIdsComponent } from './components/bricks/calling/agent-details-brick/group-names-for-ids/group-names-for-ids.component';
import { AgentListBrickComponent } from './components/bricks/calling/agent-list-brick/agent-list-brick.component';
import { AgentListSettingsComponent } from './components/bricks/calling/agent-list-brick/agent-list-settings/agent-list-settings.component';
import { CallControlBrickDefaultComponent } from './components/bricks/calling/call-control-brick/call-control-brick.default.component';
import { CallControlBrickSmallComponent } from './components/bricks/calling/call-control-brick/call-control-brick.small.component';
import { CallControlSettingsComponent } from './components/bricks/calling/call-control-brick/call-control-settings/call-control-settings.component';
import { DialpadKeyComponent } from './components/bricks/calling/call-control-brick/dialpad-key/dialpad-key.component';
import { CallHistoryBrowserBrickComponent } from './components/bricks/calling/call-history-browser-brick/call-history-browser-brick.component';
import { CallHistoryBrowserSettingsComponent } from './components/bricks/calling/call-history-browser-brick/call-history-browser-settings/call-history-browser-settings.component';
import { CallMemoCellComponent } from './components/bricks/calling/call-wait-list-brick/call-memo-cell/call-memo-cell.component';
import { CallWaitListBrickComponent } from './components/bricks/calling/call-wait-list-brick/call-wait-list-brick.component';
import { PresenceDurationBrickSettingsComponent } from './components/bricks/calling/presence-duration-brick/presence-duration-brick-settings/presence-duration-brick-settings/presence-duration-brick-settings.component';
import { PresenceDurationBrickBaseComponent } from './components/bricks/calling/presence-duration-brick/presence-duration-brick.base.component';
import { PresenceDurationBrickDefaultComponent } from './components/bricks/calling/presence-duration-brick/presence-duration-brick.default.component';
import { PresenceDurationBrickSmallComponent } from './components/bricks/calling/presence-duration-brick/presence-duration-brick.small.component';
import { SingleCallTagEditorBrickComponent } from './components/bricks/calling/single-call-tag-editor-brick/single-call-tag-editor-brick.component';
import { SingleCallTagEditorSettingsComponent } from './components/bricks/calling/single-call-tag-editor-brick/single-call-tag-editor-settings/single-call-tag-editor-settings.component';
import { SkillActiveStateCheckboxComponent } from './components/bricks/calling/skill-browser/skill-active-state-checkbox/skill-active-state-checkbox/skill-active-state-checkbox.component';
import { SkillBrowserComponent } from './components/bricks/calling/skill-browser/skill-browser.component';
import { SkillDiversionDialogComponent } from './components/bricks/calling/skill-browser/skill-diversion-dialog/skill-diversion-dialog.component';
import { StageLineAComponent } from './components/bricks/calling/stage-brick/lines/stage-line-a.component';
import { StageLineBComponent } from './components/bricks/calling/stage-brick/lines/stage-line-b.component';
import { NextDialTargetComponent } from './components/bricks/calling/stage-brick/next-dial-target/next-dial-target.component';
import { StageBrickComponent } from './components/bricks/calling/stage-brick/stage-brick.component';
import { StageSettingsComponent } from './components/bricks/calling/stage-brick/stage-settings/stage-settings.component';
import { DirectoryEditingContentComponent } from './components/bricks/directory/directory-brick/directory-brick-editing/directory-editing-content/directory-editing-content.component';
import { DirectoryBrickSettingsComponent } from './components/bricks/directory/directory-brick/directory-brick-settings/directory-brick-settings.component';
import { DirectoryHotkeySettingComponent } from './components/bricks/directory/directory-brick/directory-brick-settings/directory-hotkey-setting/directory-hotkey-setting.component';
import { DirectoryBrickComponent } from './components/bricks/directory/directory-brick/directory-brick.component';
import { DirectoryTableCellComponent } from './components/bricks/directory/directory-brick/directory-table-cell/directory-table-cell.component';
import { DirectoryTableHeaderComponent } from './components/bricks/directory/directory-brick/directory-table-header/directory-table-header.component';
import { PhoneticSliderComponent } from './components/bricks/directory/directory-brick/phonetic-slider/phonetic-slider.component';
import { DirectoryDetailsBrickComponent } from './components/bricks/directory/directory-details-brick/directory-details-brick.component';
import { DirectoryDetailsSettingComponent } from './components/bricks/directory/directory-details-brick/directory-details-setting/directory-details-setting.component';
import { DynamicActionButtonComponent } from './components/bricks/dynamic-actions-brick/dynamic-action-button/dynamic-action-button.component';
import { DynamicActionsBrickButtonsLayoutComponent } from './components/bricks/dynamic-actions-brick/dynamic-actions-brick-layouts/dynamic-actions-brick-buttons-layout/dynamic-actions-brick-buttons-layout.component';
import { DynamicActionsBrickDropdownLayoutComponent } from './components/bricks/dynamic-actions-brick/dynamic-actions-brick-layouts/dynamic-actions-brick-dropdown-layout/dynamic-actions-brick-dropdown-layout.component';
import { DynamicActionsBrickUnknownLayoutComponent } from './components/bricks/dynamic-actions-brick/dynamic-actions-brick-layouts/dynamic-actions-brick-unknown-layout/dynamic-actions-brick-unknown-layout.component';
import { DynamicActionsBrickSettingsComponent } from './components/bricks/dynamic-actions-brick/dynamic-actions-brick-settings/dynamic-actions-brick-settings.component';
import { DynamicActionsBrickComponent } from './components/bricks/dynamic-actions-brick/dynamic-actions-brick.component';
import { BrickNotAllowedBrickComponent } from './components/bricks/error/brick-not-allowed-brick/brick-not-allowed-brick.component';
import { BrickNotFoundBrickComponent } from './components/bricks/error/brick-not-found-brick/brick-not-found-brick.component';
import { BrickNotReadyBrickComponent } from './components/bricks/error/brick-not-ready-brick/brick-not-ready-brick.component';
import { InnerWallBrickSettingsComponent } from './components/bricks/layout/inner-wall-brick/inner-wall-brick-settings/inner-wall-brick-settings.component';
import { InnerWallBrickComponent } from './components/bricks/layout/inner-wall-brick/inner-wall-brick.component';
import { TabBrickSettingsComponent } from './components/bricks/layout/tab-brick/tab-brick-settings/tab-brick-settings.component';
import { TabItemSettingComponent } from './components/bricks/layout/tab-brick/tab-brick-settings/tab-item-setting/tab-item-setting.component';
import { TabBrickTitleComponent } from './components/bricks/layout/tab-brick/tab-brick-title/tab-brick-title.component';
import { TabBrickComponent } from './components/bricks/layout/tab-brick/tab-brick.component';
import { ToolbarBrickSettingsComponent } from './components/bricks/layout/toolbar-brick/toolbar-brick-settings/toolbar-brick-settings.component';
import { ToolbarBrickBaseComponent } from './components/bricks/layout/toolbar-brick/toolbar-brick.base.component';
import { ToolbarBrickDefaultComponent } from './components/bricks/layout/toolbar-brick/toolbar-brick.default.component';
import { ToolbarBrickSmallComponent } from './components/bricks/layout/toolbar-brick/toolbar-brick.small.component';
import { IsMessageActionExecutablePipe } from './components/bricks/layout/toolbar/pipes/is-message-action-executable.pipe';
import { IsUserActionExecutablePipe } from './components/bricks/layout/toolbar/pipes/is-user-action-executable.pipe';
import { IsUserActionToggledPipe } from './components/bricks/layout/toolbar/pipes/is-user-action-toggled.pipe';
import { ToolbarActionItemComponent } from './components/bricks/layout/toolbar/toolbar-action-item/toolbar-action-item.component';
import { MakeCallActionSettingsComponent } from './components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/make-call-action-settings/make-call-action-settings.component';
import { OpenDynamicLinkSettingsComponent } from './components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/open-dynamic-link-settings/open-dynamic-link-settings.component';
import { RequestUserStatusActionSettingsComponent } from './components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/request-user-status/request-user-status-action-settings.component';
import { RequeueActionSettingsComponent } from './components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/requeue-action-settings/requeue-action-settings.component';
import { SetTagActionSettingsComponent } from './components/bricks/layout/toolbar/toolbar-settings/action-specific-settings/set-tag-action-settings/set-tag-action-settings.component';
import { ActionComponent } from './components/bricks/layout/toolbar/toolbar-settings/action/action.component';
import { ToolbarItemSettingComponent } from './components/bricks/layout/toolbar/toolbar-settings/toolbar-item-setting/toolbar-item-setting.component';
import { ToolbarComponent } from './components/bricks/layout/toolbar/toolbar.component';
import { SkillBrowserMailDetailComponent } from './components/bricks/mailing/skill-browser/skill-browser-mail-detail/skill-browser-mail-detail/skill-browser-mail-detail.component';
import { SkillBrowserMailComponent } from './components/bricks/mailing/skill-browser/skill-browser-mail/skill-browser-mail.component';
import { MailWaitlistComponent } from './components/bricks/mailing/waitlist/mail-waitlist/mail-waitlist.component';
import { GrafanaDashboardViewerBrickComponent } from './components/bricks/statistics/grafana-dashboard-viewer-brick/grafana-dashboard-viewer-brick.component';
import { GrafanaDashboardViewerSettingsComponent } from './components/bricks/statistics/grafana-dashboard-viewer-brick/grafana-dashboard-viewer-settings/grafana-dashboard-viewer-settings.component';
import { AlternativeDeviceEditorComponent } from './components/bricks/user/user-status-brick/alternative-device-editor/alternative-device-editor.component';
import { UserStatusBrickSettingsComponent } from './components/bricks/user/user-status-brick/user-status-brick-settings/user-status-brick-settings.component';
import { UserStatusBrickDefaultComponent } from './components/bricks/user/user-status-brick/user-status-brick.default.component';
import { UserStatusBrickSmallComponent } from './components/bricks/user/user-status-brick/user-status-brick.small.component';
import { ClockBrickComponent } from './components/bricks/utility/clock-brick/clock-brick.component';
import { DragContainerComponent } from './components/bricks/utility/drag-container/drag-container.component';
import { DropContainerComponent } from './components/bricks/utility/drop-container/drop-container.component';
import { GridWaitForDataComponent } from './components/bricks/utility/grid-wait-for-data/grid-wait-for-data.component';
import { HighlightTextComponent } from './components/bricks/utility/highlight-text/highlight-text.component';
import { LoadTestBrickComponent } from './components/bricks/utility/load-test-brick/load-test-brick.component';
import { LoadingIndicatorComponent } from './components/bricks/utility/loading-indicator/loading-indicator.component';
import { WebviewerBrickSettingsComponent } from './components/bricks/webviewer-brick/webviewer-brick-settings/webviewer-brick-settings.component';
import { WebviewerBrickComponent } from './components/bricks/webviewer-brick/webviewer-brick.component';
import { AppointmentScheduleComponent } from './components/contact-status/appointments/appointment-schedule/appointment-schedule.component';
import { AppointmentsComponent } from './components/contact-status/appointments/appointments.component';
import { LineStatusComponent } from './components/contact-status/line-status/line-status.component';
import { MobileStatusComponent } from './components/contact-status/mobile-status/mobile-status.component';
import { PresenceStatusForwardDestinationComponent } from './components/contact-status/presence-status-forward-destination/presence-status-forward-destination.component';
import { PresenceStatusNoteComponent } from './components/contact-status/presence-status-note/presence-status-note.component';
import { PresenceStatusComponent } from './components/contact-status/presence-status/presence-status.component';
import { BrickContainerComponent } from './components/dashboard/brick-container/brick-container.component';
import { BrickControlOverlayComponent } from './components/dashboard/brick-container/brick-control-overlay/brick-control-overlay.component';
import { BrickCommonSettingsComponent } from './components/dashboard/brick-settings/brick-common-settings/brick-common-settings.component';
import { BrickSettingButtonComponent } from './components/dashboard/brick-settings/brick-setting-button/brick-setting-button.component';
import { BrickSettingsComponent } from './components/dashboard/brick-settings/brick-settings.component';
import { BrickSpecificTypeSettingsComponent } from './components/dashboard/brick-settings/brick-specific-type-settings/brick-specific-type-settings.component';
import { BrickWallComponent } from './components/dashboard/brick-wall/brick-wall.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { HomeBarItemComponent } from './components/dashboard/home-bar/home-bar-item/home-bar-item.component';
import { HomeBarComponent } from './components/dashboard/home-bar/home-bar.component';
import { PilotPanelComponent } from './components/dashboard/pilot-panel/pilot-panel.component';
import { AcceptOrDialDialogComponent } from './components/dialogs/accept-or-dial-dialog/accept-or-dial-dialog.component';
import { AppointmentSchedulerDialogComponent } from './components/dialogs/appointment-scheduler-dialog/appointment-scheduler-dialog.component';
import { CallingToastDialogComponent } from './components/dialogs/calling-toast-dialog/calling-toast-dialog.component';
import { ChatConfirmCloseDialogComponent } from './components/dialogs/chat/chat-confirm-close-dialog/chat-confirm-close-dialog.component';
import { ChatGuestInfoComponent } from './components/dialogs/chat/chat-details/chat-guest-info.component';
import { ChatDialogComponent } from './components/dialogs/chat/chat-dialog/chat-dialog.component';
import { ChatListItemComponent } from './components/dialogs/chat/chat-list-item/chat-list-item.component';
import { ChatMessageComponent } from './components/dialogs/chat/chat-message/chat-message.component';
import { UnreadMessagesBadgeComponent } from './components/dialogs/chat/unread-messages-badge/unread-messages-badge.component';
import { ConfirmDialogComponent } from './components/dialogs/confirm-dialog/confirm-dialog.component';
import { DirectoryEditingConfirmationDialogComponent } from './components/dialogs/directory-editing/directory-editing-confirmation-dialog/directory-editing-confirmation-dialog.component';
import { DirectoryEditingDialogComponent } from './components/dialogs/directory-editing/directory-editing-dialog/directory-editing-dialog.component';
import { DirectoryEditingItemComponent } from './components/dialogs/directory-editing/directory-editing-item/directory-editing-item.component';
import { AttachmentBarComponent } from './components/dialogs/email/attachment-bar/attachment-bar.component';
import { AttachmentItemComponent } from './components/dialogs/email/attachment-item/attachment-item.component';
import { AttachmentUploadBarComponent } from './components/dialogs/email/attachment-upload-bar/attachment-upload-bar.component';
import { EmailCategoryComponent } from './components/dialogs/email/email-category/email-category.component';
import { EmailConfirmCloseDialogComponent } from './components/dialogs/email/email-confirm-close-dialog/email-confirm-close-dialog.component';
import { EmailDialogComponent } from './components/dialogs/email/email-dialog/email-dialog.component';
import { EmailEditorWindowComponent } from './components/dialogs/email/email-editor-window/email-editor-window.component';
import { EmailListDialogComponent } from './components/dialogs/email/email-list/email-list-dialog.component';
import { EmailListItemComponent } from './components/dialogs/email/email-list/email-list-item/email-list-item.component';
import { EmailReaderActionButtonComponent } from './components/dialogs/email/email-reader-action-button/email-reader-action-button.component';
import { EmailReaderWindowComponent } from './components/dialogs/email/email-reader-window/email-reader-window.component';
import { UploadImageDialogComponent } from './components/dialogs/email/upload-dialog/upload-image-dialog.component';
import { UploadComponent } from './components/dialogs/email/upload-dialog/upload/upload.component';
import { HotkeyDuplicateDialogComponent } from './components/dialogs/hotkey-duplicate-dialog/hotkey-duplicate-dialog.component';
import { QueueSelectionDialogComponent } from './components/dialogs/queue-selection-dialog/queue-selection-dialog.component';
import { SignOutDialogComponent } from './components/dialogs/sign-out-dialog/sign-out-dialog.component';
import { AvatarStatusSelectionComponent } from './components/fragments/avatar-status-selection/avatar-status-selection.component';
import { AvatarComponent } from './components/fragments/avatar/avatar.component';
import { CallAgentComponent } from './components/fragments/call-agent/call-agent.component';
import { CallMemoComponent } from './components/fragments/call-memo/call-memo.component';
import { CallProgressInfoComponent } from './components/fragments/call-progress-info/call-progress-info.component';
import { CallProgressItemComponent } from './components/fragments/call-progress-info/call-progress-item/call-progress-item.component';
import { CallTimeComponent } from './components/fragments/call-time/call-time.component';
import { GridFilterComponent } from './components/fragments/grid-filter/grid-filter.component';
import { ConversationTypeComponent } from './components/fragments/icons/conversation-type/conversation-type.component';
import { IciTypeComponent } from './components/fragments/icons/ici-type/ici-type.component';
import { PeerInfoComponent } from './components/fragments/peer-info/peer-info/peer-info.component';
import { RecordingComponent } from './components/fragments/recording/recording.component';
import { SkillShortInfoIconComponent } from './components/fragments/skill-short-info/skill-short-info-icon/skill-short-info-icon/skill-short-info-icon.component';
import { SkillShortInfoComponent } from './components/fragments/skill-short-info/skill-short-info.component';
import { UserPasswordComponent } from './components/fragments/user-password/user-password.component';
import { UserShortInfoComponent } from './components/fragments/user-short-info/user-short-info.component';
import { UserStatusClassDirective } from './components/fragments/user-status/user-status-class.directive';
import { UserStatusDropdownComponent } from './components/fragments/user-status/user-status-dropdown/user-status-dropdown.component';
import { UserStatusProgressbarComponent } from './components/fragments/user-status/user-status-progressbar/user-status-progressbar.component';
import { AutoFitFontSizeDirective } from './components/general-directives/auto-fit-font-size.directive';
import { AutoFocusDirective } from './components/general-directives/auto-focus.directive';
import { BlurOnEnterDirective } from './components/general-directives/blur-on-enter.directive';
import { CellDoubleClickDirective } from './components/general-directives/cell-double-click.directive';
import { ComponentHostDirective } from './components/general-directives/component-host.directive';
import { ClickEventDebounceDirective } from './components/general-directives/debounce/click-event-debounce.directive';
import { DebouncedInputDirective } from './components/general-directives/debounce/debounced-input.directive';
import { ValueChangeDebounceDirective } from './components/general-directives/debounce/value-change-debounce.directive';
import { ExternalLinkDirective } from './components/general-directives/external-link.directive';
import { KendoInfinityScrollingDirective } from './components/general-directives/kendo-infinity-scrolling.directive';
import { LazyLoadIfDirective } from './components/general-directives/lazy-load-if.directive';
import { NgVarDirective } from './components/general-directives/ng-var.directive';
import { OutsideClickDirective } from './components/general-directives/outside-click-component.directive';
import { PasswordTextboxDirective } from './components/general-directives/password-textbox.directive';
import { PersistKendoGridDirective } from './components/general-directives/persist-kendo-grid.directive';
import { TooltipDialogDirective } from './components/general-directives/tooltip-dialog.directive';
import { VirtualScrollKendoGridDirective } from './components/general-directives/virtual-scroll-kendo-grid.directive';
import { AssignedMailToastComponent } from './components/notifications/assigned-mail-toast/assigned-mail-toast.component';
import { ChatToastComponent } from './components/notifications/chat-toast/chat-toast.component';
import { OutboundConfirmationToastComponent } from './components/notifications/outbound-confirmation-toast/outbound-confirmation-toast.component';
import { RingingToastComponent } from './components/notifications/ringing-toast/ringing-toast.component';
import { UnreachableToastComponent } from './components/notifications/unreachable-toast/unreachable-toast.component';
import { WaitingItemComponent } from './components/notifications/waiting-item/waiting-item.component';
import { AudioDeviceSelectorComponent } from './components/settings/global-settings/audio-settings/audio-device-selector/audio-device-selector.component';
import { AudioSettingsComponent } from './components/settings/global-settings/audio-settings/audio-settings.component';
import { HeadsetManufacturerSelectorComponent } from './components/settings/global-settings/audio-settings/headset-manufacturer-selector/headset-manufacturer-selector.component';
import { BrickwallSettingsComponent } from './components/settings/global-settings/brickwall-settings/brickwall-settings.component';
import { CommonSettingsComponent } from './components/settings/global-settings/common-settings/common-settings.component';
import { GlobalSettingsComponent } from './components/settings/global-settings/global-settings.component';
import { AgentGreetingWaveComponent } from './components/settings/global-settings/greeting-settings/agent-greeting-wave/agent-greeting-wave.component';
import { AgentGreetingConfigurationIciComponent } from './components/settings/global-settings/greeting-settings/greeting-component/agent-greeting-configuration-list/agent-greeting-configuration-ici/agent-greeting-configuration-ici.component';
import { AgentGreetingWaveDropComponent } from './components/settings/global-settings/greeting-settings/greeting-component/agent-greeting-configuration-list/agent-greeting-configuration-ici/agent-greeting-wave-drop/agent-greeting-wave-drop.component';
import { AgentGreetingConfigurationListComponent } from './components/settings/global-settings/greeting-settings/greeting-component/agent-greeting-configuration-list/agent-greeting-configuration-list.component';
import { AgentGreetingWaveListComponent } from './components/settings/global-settings/greeting-settings/greeting-component/agent-greeting-wave-list/agent-greeting-wave-list.component';
import { GreetingSettingsComponent } from './components/settings/global-settings/greeting-settings/greeting-component/greeting-settings.component';
import { GreetingUploadBarDialogComponent } from './components/settings/global-settings/greeting-settings/greeting-upload-bar-dialog/greeting-upload-bar-dialog.component';
import { HotkeySettingComponent } from './components/settings/global-settings/keyboard-settings/hotkey-setting/hotkey-setting.component';
import { KeyboardSettingsComponent } from './components/settings/global-settings/keyboard-settings/keyboard-settings.component';
import { SystemMonitorStatusIconComponent } from './components/systemMonitoring/system-monitor-overview/status-icon/system-monitor-status-icon.component';
import { SystemMonitorOverviewComponent } from './components/systemMonitoring/system-monitor-overview/system-monitor-overview.component';
import { ClearButtonComponent } from './components/utility/clear-button/clear-button.component';
import { ConfigurableEditorSettingPartComponent } from './components/utility/configurableEditor/configurable-editor-setting-part/configurable-editor-setting-part.component';
import { ConfigurableEditorComponent } from './components/utility/configurableEditor/configurable-editor/configurable-editor.component';
import { NumberFormatConverterPipe } from './components/utility/configurableEditor/configurable-editor/pipes/number-format-converter.pipe';
import { SelectorValuesConverterPipe } from './components/utility/configurableEditor/configurable-editor/pipes/selector-values-converter.pipe';
import { DialogTooltipComponent } from './components/utility/dialog-tooltip/dialog-tooltip.component';
import { BaseDirectoryEditComponent } from './components/utility/directory-column-edit/base-directory-edit/base-directory-edit.component';
import { DirectoryColumnEditComponent } from './components/utility/directory-column-edit/directory-column-edit.component';
import { NonEditableFieldComponent } from './components/utility/directory-column-edit/non-editable-field/non-editable-field.component';
import { OrgFieldColumnEditComponent } from './components/utility/directory-column-edit/orgfield-column-edit/orgfield-column-edit.component';
import { StringColumnEditComponent } from './components/utility/directory-column-edit/string-column-edit/string-column-edit.component';
import { StringMultilineColumnEditComponent } from './components/utility/directory-column-edit/string-multiline-column-edit/string-multiline-column-edit.component';
import { EmailMultiselectComponent } from './components/utility/email-multiselect/email-multiselect.component';
import { IconPickerComponent } from './components/utility/icon-picker/icon-picker.component';
import { IconWithColoredSubiconComponent } from './components/utility/icon-with-colored-subicon/icon-with-colored-subicon.component';
import { IconWithSubiconComponent } from './components/utility/icon-with-subicon/icon-with-subicon.component';
import { InputFieldComponent } from './components/utility/input-field/input-field.component';
import { NoValueSelectedInfoComponent } from './components/utility/no-value-selected-info/no-value-selected-info.component';
import { SamwebWindowComponent } from './components/utility/samweb-window/samweb-window.component';
import { SearchbarComponent } from './components/utility/searchbar/searchbar/searchbar.component';
import { SkillFilterBarComponent } from './components/utility/skill-filter-bar/skill-filter-bar.component';
import { TextAreaComponent } from './components/utility/text-area/text-area.component';
import { TextEditorComponent } from './components/utility/text-editor/text-editor.component';
import { TextWithOverflowTooltipComponent } from './components/utility/text-with-overflow-tooltip/text-with-overflow-tooltip.component';
import { TooltipContainerComponent } from './components/utility/tooltip-container/tooltip-container.component';
import { VirtualScrollListComponent } from './components/utility/virtual-scroll-list/virtual-scroll-list.component';
import { brickDefinitionsLoader, configureMoments } from './helper/appInitializerFunctions';
import { AcdCurrentDevicePipe } from './pipes/acd-extension/acd-current-device.pipe';
import { AcdExtensionToAcdNumberPipe } from './pipes/acd-extension/acd-extension-to-acd-number.pipe';
import { AcdLineToDisplayNumberPipe } from './pipes/acd-extension/acd-line-to-display-number.pipe';
import { ToAcdTypePipe } from './pipes/acd-extension/to-acd-type.pipe';
import { IciTypeByNamePipe } from './pipes/agent-greetings/ici-type-by-name.pipe';
import { ToFreeBusyStateClass } from './pipes/appointments/to-free-busy-state-class.pipe';
import { BrickIdToGridsterConfigPipe } from './pipes/bricks/brick-id-to-gridster-config.pipe';
import { InstanceIdToBrickDescriptionPipe } from './pipes/bricks/instance-id-brick-description.pipe';
import { InstanceIdToBrickTitlePipe } from './pipes/bricks/instance-id-tab-brick-title.pipe';
import { ToProgressStatePipe } from './pipes/call-history/to-progress-state.pipe';
import { I18nSortPipe } from './pipes/collections/i18n-sort.pipe';
import { IsAnonymousPipe } from './pipes/comparison/is-anonymous.pipe';
import { SafeHtmlPipe } from './pipes/converter/safe-html.pipe';
import { SafeStylePipe } from './pipes/converter/safe-style.pipe';
import { StringToNumberPipe } from './pipes/converter/string-to-number.pipe';
import { StripHtmlPipe } from './pipes/converter/strip-html.pipe';
import { ToMomentPipe } from './pipes/converter/to-moment.pipe';
import { ToUtcMomentPipe } from './pipes/converter/to-utc-moment.pipe';
import { CustomColumnNamePipe } from './pipes/custom-column-name.pipe';
import { DebounceTimePipe } from './pipes/debounce-time.pipe';
import { ToBehaviourSubjectPipe } from './pipes/datatypes/to-behaviour-subject.pipe';
import { AvailableDirectoryDataForColumnPipe } from './pipes/directory/available-directory-data-for-column.pipe';
import { AvailableDirectoryDataForEntryPipe } from './pipes/directory/available-directory-data-for-entry.pipe';
import { DialTargetToPeerInfoPipe } from './pipes/directory/dial-target-to-peer-info.pipe';
import { DirectoryColumnComparatorPipe } from './pipes/directory/directory-column-comparator.pipe';
import { DirectoryColumnHeaderIsCallablePipe } from './pipes/directory/directory-column-header-is-callable.pipe';
import { DirectoryColumnHeaderIsSearchablePipe } from './pipes/directory/directory-column-header-is-searchable.pipe';
import { DirectoryColumnIsRestrictedPipe } from './pipes/directory/directory-column-is-restricted.pipe';
import { DirectoryColumnIsSearchablePipe } from './pipes/directory/directory-column-is-searchable.pipe';
import { DirectoryColumnOrganisationUnitPipe } from './pipes/directory/directory-column-organisation-unit.pipe';
import { DirectoryColumnStructureDataPipe } from './pipes/directory/directory-column-structure-data.pipe';
import { DirectoryColumnStructureValuePipe } from './pipes/directory/directory-column-structure-value.pipe';
import { DirectoryColumnTitlePipe } from './pipes/directory/directory-column-title.pipe';
import { DirectoryColumnValuePipe } from './pipes/directory/directory-column-value.pipe';
import { DirectoryInfoLastEditingUserPipe } from './pipes/directory/directory-info-last-editing-user.pipe';
import { DirectoryInfoLastTimeEditedPipe } from './pipes/directory/directory-info-last-time-edited.pipe';
import { AreAllFeaturesEnabledPipe } from './pipes/features/are-all-features-enabled.pipe';
import { IsAnyFeatureEnabledPipe } from './pipes/features/is-any-feature-enabled.pipe';
import { IsFeatureEnabledPipe } from './pipes/features/is-feature-enabled.pipe';
import { AddBracketsPipe } from './pipes/format/add-brackets.pipe';
import { FormatBytesPipe } from './pipes/format/format-bytes.pipe';
import { TranslateCallTagPipe } from './pipes/format/translate-call-tag.pipe';
import { IconNameToIconDefinitionPipe } from './pipes/icons/icon-name-to-icon-definition.pipe';
import { IsDynamicActionExecutablePipe } from './pipes/is-dynamic-action-executable.pipe';
import { IsDynamicActionToggledPipe } from './pipes/is-dynamic-action-toggled.pipe';
import { IsNilPipe } from './pipes/lodash/is-nil.pipe';
import { IsEmptyPipe } from './pipes/lodash/is-empty.pipe';
import { SomePipe } from './pipes/lodash/some.pipe';
import { ToQueueTypePipe } from './pipes/queues/to-queue-type.pipe';
import { IciToCallDirectionTextPipe } from './pipes/sccs1/ici-call-direction-text.pipe';
import { IciExtraInfoTextPipe } from './pipes/sccs1/ici-extra-info-text.pipe';
import { IciTextPipe } from './pipes/sccs1/ici-text.pipe';
import { HighlightTextPipe } from './pipes/search/highlight-text.pipe';
import { CallingSkillsPipe } from './pipes/skills/calling-skills.pipe';
import { IsSkillDiversionAllowedPipe } from './pipes/skills/is-skill-diversion-allowed.pipe';
import { MailingSkillsPipe } from './pipes/skills/mailing-skills.pipe';
import { SkillIdToShortSkillInfoPipe } from './pipes/skills/skill-id-to-short-skill-info.pipe';
import { AmTimeAgoUpdateEverySecondPipe } from './pipes/time/am-time-ago-every-second.pipe';
import { AmTimeInThePastUpdateEveryMinutePipe } from './pipes/time/am-time-in-the-past-update-every-minute.pipe';
import { DurationToTextPipe } from './pipes/time/duration-to-text.pipe';
import { SingleTimeUnitPipe } from './pipes/time/single-time-unit.pipe';
import { SingleUnitDurationToTextPipe } from './pipes/time/single-unit-duration-to-text.pipe';
import { UserIdToUserShortInfoPipe } from './pipes/users/user-id-to-user-short-info.pipe';
import { FaviconService } from './services/app/favicon.service';
import { TitleService } from './services/app/title.service';
import { AudioService } from './services/audio/audio.service';
import { RingerService } from './services/audio/ringer.service';
import { ChatJsApiService } from './services/chat/js-api/chat-js-api-service';
import { HotkeyService } from './services/hotkey/hotkey.service';
import { SamwinInterceptor } from './services/interceptors/samwin.interceptor';
import { TabClosingPreventionService } from './services/tab-closing-prevention/tab-closing-prevention.service';
import { TabSleepPreventionService } from './services/tab-sleep-prevention/tab-sleep-prevention.service';
import { TeamsSingleSignOnService } from './services/teams-sso/teams-single-sign-on.service';
import { DynamicLocaleId } from './services/translation/DynamicLocaleId';
import { KendoMessageService } from './services/translation/kendo-message.service';
import { MultiTranslateHttpLoader } from './services/translation/multi-http-loader';
import { TranslationConfigurationService } from './services/translation/translation-configuration.service';
import { UpdateService } from './services/update/update.service';
import { StoreModule } from './store/store.module';

export function createTranslateLoader(http: HttpClient) {
   return new MultiTranslateHttpLoader(http, [
      { prefix: './assets/i18n/', suffix: '.json' },
      { prefix: './assets/i18n/kendo/calendar/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/colorgradient/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/dateinput/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/datepicker/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/datetimepicker/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/dialog/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/dropdowns/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/editor/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/grid/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/notification/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/numerictextbox/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/pager/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/rangeslider/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/scheduler/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/slider/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/switch/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/textbox/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/timepicker/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/toolbar/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/tooltip/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/treelist/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/upload/', suffix: '.yml' },
      { prefix: './assets/i18n/kendo/window/', suffix: '.yml' },
   ]);
}

// noinspection AngularInvalidImportedOrDeclaredSymbol
@NgModule({
   declarations: [
      AppComponent,
      AuthenticationProcessComponent,
      DashboardComponent,
      HomeBarComponent,
      BrickWallComponent,
      BrickContainerComponent,
      ComponentHostDirective,
      PilotPanelComponent,
      CallControlBrickDefaultComponent,
      CallControlBrickSmallComponent,
      AddBrickDialogComponent,
      BrickDefinitionItemComponent,
      OutsideClickDirective,
      ClockBrickComponent,
      CallWaitListBrickComponent,
      BrickNotAllowedBrickComponent,
      BrickNotReadyBrickComponent,
      BrickNotFoundBrickComponent,
      ConversationTypeComponent,
      IciTypeComponent,
      CallTimeComponent,
      IciTextPipe,
      IciExtraInfoTextPipe,
      IciToCallDirectionTextPipe,
      SkillBrowserComponent,
      SkillShortInfoComponent,
      TooltipDialogDirective,
      SkillDiversionDialogComponent,
      IsFeatureEnabledPipe,
      AreAllFeaturesEnabledPipe,
      IsAnyFeatureEnabledPipe,
      AutoFocusDirective,
      IsSkillDiversionAllowedPipe,
      MailingSkillsPipe,
      CallingSkillsPipe,
      PersistKendoGridDirective,
      PasswordTextboxDirective,
      AcdSelectionFormComponent,
      AuthorizationFormComponent,
      AuthenticationLoadingComponent,
      ToAcdTypePipe,
      AcdExtensionToAcdNumberPipe,
      ToQueueTypePipe,
      AgentListBrickComponent,
      DirectoryBrickComponent,
      DirectoryTableCellComponent,
      DirectoryColumnTitlePipe,
      DialTargetToPeerInfoPipe,
      LoadingIndicatorComponent,
      GridWaitForDataComponent,
      HomeBarItemComponent,
      DialpadKeyComponent,
      KendoInfinityScrollingDirective,
      AutoFitFontSizeDirective,
      AgentStatusComponent,
      ToUtcMomentPipe,
      GridFilterComponent,
      PresenceStatusComponent,
      LineStatusComponent,
      MobileStatusComponent,
      PresenceStatusNoteComponent,
      PresenceStatusForwardDestinationComponent,
      GlobalSettingsComponent,
      CommonSettingsComponent,
      BrickwallSettingsComponent,
      KeyboardSettingsComponent,
      AudioSettingsComponent,
      BrickSettingsComponent,
      BrickCommonSettingsComponent,
      CallControlSettingsComponent,
      BrickSpecificTypeSettingsComponent,
      AgentListSettingsComponent,
      DirectoryBrickSettingsComponent,
      RingingToastComponent,
      OutboundConfirmationToastComponent,
      ChatToastComponent,
      HotkeySettingComponent,
      TabBrickComponent,
      TabBrickSettingsComponent,
      TabItemSettingComponent,
      IconPickerComponent,
      IconNameToIconDefinitionPipe,
      ToolbarBrickSettingsComponent,
      BrickSettingButtonComponent,
      TabBrickTitleComponent,
      InstanceIdToBrickTitlePipe,
      InstanceIdToBrickDescriptionPipe,
      ToolbarItemSettingComponent,
      ToolbarActionItemComponent,
      IsUserActionExecutablePipe,
      IsMessageActionExecutablePipe,
      IsUserActionToggledPipe,
      SingleCallTagEditorBrickComponent,
      SingleCallTagEditorSettingsComponent,
      AudioDeviceSelectorComponent,
      HeadsetManufacturerSelectorComponent,
      UserStatusBrickDefaultComponent,
      UserStatusBrickSmallComponent,
      UserStatusBrickSettingsComponent,
      UserStatusDropdownComponent,
      AlternativeDeviceEditorComponent,
      ConfigurableEditorSettingPartComponent,
      ConfigurableEditorComponent,
      StringToNumberPipe,
      NumberFormatConverterPipe,
      SelectorValuesConverterPipe,
      SafeHtmlPipe,
      StripHtmlPipe,
      SafeStylePipe,
      UserStatusClassDirective,
      UserIdToUserShortInfoPipe,
      AcdLineToDisplayNumberPipe,
      InnerWallBrickComponent,
      InnerWallBrickSettingsComponent,
      BrickIdToGridsterConfigPipe,
      CallHistoryBrowserBrickComponent,
      SkillIdToShortSkillInfoPipe,
      CallHistoryBrowserSettingsComponent,
      CallProgressInfoComponent,
      CallMemoComponent,
      CallAgentComponent,
      ToProgressStatePipe,
      CallProgressItemComponent,
      SystemMonitorOverviewComponent,
      SystemMonitorStatusIconComponent,
      QueueSelectionDialogComponent,
      PeerInfoComponent,
      RequeueActionSettingsComponent,
      SignOutDialogComponent,
      PresenceDurationBrickBaseComponent,
      DebouncedInputDirective,
      CallMemoCellComponent,
      AgentDetailsBrickComponent,
      AgentDetailsSettingsComponent,
      GroupNamesForIdsComponent,
      CallHistoryForAgentComponent,
      NoValueSelectedInfoComponent,
      WebviewerBrickComponent,
      WebviewerBrickSettingsComponent,
      HighlightTextPipe,
      UnreachableToastComponent,
      LoadTestBrickComponent,
      AmTimeAgoUpdateEverySecondPipe,
      ToFreeBusyStateClass,
      StageBrickComponent,
      StageSettingsComponent,
      StageLineAComponent,
      StageLineBComponent,
      CustomColumnNamePipe,
      RequestUserStatusActionSettingsComponent,
      OpenDynamicLinkSettingsComponent,
      MakeCallActionSettingsComponent,
      SetTagActionSettingsComponent,
      VirtualScrollKendoGridDirective,
      DurationToTextPipe,
      DynamicActionsBrickComponent,
      DynamicActionsBrickSettingsComponent,
      DynamicActionsBrickButtonsLayoutComponent,
      DynamicActionsBrickUnknownLayoutComponent,
      DynamicActionsBrickDropdownLayoutComponent,
      BlurOnEnterDirective,
      UserPasswordComponent,
      UserShortInfoComponent,
      AcdCurrentDevicePipe,
      HotkeyDuplicateDialogComponent,
      BrickControlOverlayComponent,
      I18nSortPipe,
      AvatarComponent,
      RecordingComponent,
      ClearButtonComponent,
      HighlightTextComponent,
      DirectoryHotkeySettingComponent,
      AcceptOrDialDialogComponent,
      EmailDialogComponent,
      AppointmentsComponent,
      ToMomentPipe,
      AppointmentScheduleComponent,
      AppointmentSchedulerDialogComponent,
      AppointmentsComponent,
      ToMomentPipe,
      AppointmentScheduleComponent,
      AppointmentSchedulerDialogComponent,
      AppointmentsComponent,
      ToMomentPipe,
      AppointmentScheduleComponent,
      AppointmentSchedulerDialogComponent,
      SkillBrowserMailComponent,
      SkillBrowserMailDetailComponent,
      SkillShortInfoIconComponent,
      SkillActiveStateCheckboxComponent,
      MailWaitlistComponent,
      SearchbarComponent,
      SkillFilterBarComponent,
      UploadImageDialogComponent,
      UploadComponent,
      EmailReaderWindowComponent,
      EmailEditorWindowComponent,
      EmailReaderActionButtonComponent,
      EmailListDialogComponent,
      AttachmentItemComponent,
      TextWithOverflowTooltipComponent,
      AttachmentBarComponent,
      InputFieldComponent,
      AttachmentUploadBarComponent,
      IconWithSubiconComponent,
      TextEditorComponent,
      FormatBytesPipe,
      TranslateCallTagPipe,
      AddBracketsPipe,
      EmailListItemComponent,
      AssignedMailToastComponent,
      EmailConfirmCloseDialogComponent,
      GrafanaDashboardViewerBrickComponent,
      GrafanaDashboardViewerSettingsComponent,
      NgVarDirective,
      ExternalLinkDirective,
      IconWithColoredSubiconComponent,
      PhoneticSliderComponent,
      ToolbarComponent,
      ActionComponent,
      DirectoryEditingDialogComponent,
      DirectoryEditingContentComponent,
      SamwebWindowComponent,
      DirectoryEditingItemComponent,
      DirectoryColumnEditComponent,
      StringColumnEditComponent,
      OrgFieldColumnEditComponent,
      BaseDirectoryEditComponent,
      NonEditableFieldComponent,
      DirectoryColumnStructureDataPipe,
      DirectoryColumnOrganisationUnitPipe,
      DirectoryColumnComparatorPipe,
      DirectoryEditingConfirmationDialogComponent,
      DirectoryColumnStructureValuePipe,
      DirectoryTableHeaderComponent,
      DirectoryColumnValuePipe,
      DirectoryColumnIsSearchablePipe,
      DirectoryDetailsBrickComponent,
      ConfirmDialogComponent,
      DirectoryColumnIsRestrictedPipe,
      DirectoryDetailsSettingComponent,
      TextAreaComponent,
      StringMultilineColumnEditComponent,
      DirectoryInfoLastTimeEditedPipe,
      DirectoryInfoLastEditingUserPipe,
      LazyLoadIfDirective,
      ChatDialogComponent,
      ChatListItemComponent,
      ChatGuestInfoComponent,
      UnreadMessagesBadgeComponent,
      ChatMessageComponent,
      ChatConfirmCloseDialogComponent,
      IsDynamicActionExecutablePipe,
      IsDynamicActionToggledPipe,
      NextDialTargetComponent,
      IsAnonymousPipe,
      ToolbarBrickBaseComponent,
      ToolbarBrickDefaultComponent,
      ToolbarBrickSmallComponent,
      AvatarStatusSelectionComponent,
      ValueChangeDebounceDirective,
      SingleUnitDurationToTextPipe,
      SingleTimeUnitPipe,
      PresenceDurationBrickSettingsComponent,
      PresenceDurationBrickDefaultComponent,
      PresenceDurationBrickSmallComponent,
      UserStatusProgressbarComponent,
      AmTimeInThePastUpdateEveryMinutePipe,
      EmailMultiselectComponent,
      ClickEventDebounceDirective,
      CellDoubleClickDirective,
      WaitingItemComponent,
      DirectoryColumnHeaderIsSearchablePipe,
      DirectoryColumnHeaderIsCallablePipe,
      CallingToastDialogComponent,
      DynamicActionButtonComponent,
      ToBehaviourSubjectPipe,
      GreetingSettingsComponent,
      GreetingUploadBarDialogComponent,
      AgentGreetingWaveListComponent,
      AgentGreetingConfigurationListComponent,
      AgentGreetingConfigurationIciComponent,
      AgentGreetingWaveDropComponent,
      IsNilPipe,
      VirtualScrollListComponent,
      AgentGreetingWaveComponent,
      DragContainerComponent,
      DropContainerComponent,
      AvailableDirectoryDataForColumnPipe,
      AvailableDirectoryDataForEntryPipe,
      DebounceTimePipe,
      SomePipe,
      IsEmptyPipe,
      TooltipContainerComponent,
      EmailCategoryComponent,
      DialogTooltipComponent,
      IciTypeByNamePipe,
   ],
   imports: [
      BrowserModule,
      AppRoutingModule,
      ApiModule,
      HttpClientModule,
      TranslateModule.forRoot({
         loader: {
            provide: TranslateLoader,
            useFactory: createTranslateLoader,
            deps: [HttpClient],
         },
      }),
      StoreModule,
      FormsModule,
      ReactiveFormsModule,
      BrowserAnimationsModule,
      InputsModule,
      DialogsModule,
      ButtonsModule,
      PopupModule,
      TabStripModule,
      TooltipModule,
      FontAwesomeModule,
      GridsterModule,
      LayoutModule,
      FlexLayoutModule,
      GridModule,
      DropDownsModule,
      SharedModule,
      MomentModule,
      BodyModule,
      InfiniteScrollModule,
      RowFilterModule,
      InfiniteScrollModule,
      ToastrModule.forRoot({
         countDuplicates: true,
         resetTimeoutOnDuplicate: true,
         preventDuplicates: true,
      }),
      ToastContainerModule,
      ServiceWorkerModule.register('webagent-worker.js', { enabled: true }),
      SortableModule,
      EditorModule,
      ListViewModule,
      SchedulerModule,
      TreeViewModule,
      UploadModule,
      FileSelectModule,
      DragDropModule,
      IndicatorsModule,
   ],
   providers: [
      {
         provide: BASE_PATH,
         useValue: environment.API_BASE_PATH,
         multi: true,
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: SamwinInterceptor,
         multi: true,
      },
      {
         provide: APP_INITIALIZER,
         useFactory: (
            store: Store,
            tabClosingPreventionService: TabClosingPreventionService,
            tabSleepPreventionService: TabSleepPreventionService,
            audioService: AudioService,
            ringerService: RingerService,
            translationConfigurationService: TranslationConfigurationService,
            kendoMessageService: KendoMessageService,
            hotkeyService: HotkeyService,
            updateService: UpdateService,
            teamsSingleSignOnService: TeamsSingleSignOnService,
            chatJsApiService: ChatJsApiService,
            titleService: TitleService,
            faviconService: FaviconService
         ) => () => {
            brickDefinitionsLoader(store);
            configureMoments();
            tabClosingPreventionService.init();
            tabSleepPreventionService.init();
            audioService.initializeAudioService();
            ringerService.initializeRingerService();
            translationConfigurationService.initialize();
            kendoMessageService.initialize();
            updateService.initialize();
            teamsSingleSignOnService.initialize();
            chatJsApiService.initialize();
         },
         deps: [
            Store,
            TabClosingPreventionService,
            TabSleepPreventionService,
            AudioService,
            RingerService,
            TranslationConfigurationService,
            KendoMessageService,
            HotkeyService,
            UpdateService,
            TeamsSingleSignOnService,
            ChatJsApiService,
            TitleService,
            FaviconService,
         ],
         multi: true,
      },
      { provide: MessageService, useExisting: KendoMessageService },
      // make sure you use this for Hash Urls rather than HTML 5 routing
      { provide: LocationStrategy, useClass: HashLocationStrategy },
      CookieService,
      // NOTE: Locale only changes after Reload
      {
         provide: LOCALE_ID,
         useClass: DynamicLocaleId,
         deps: [TranslateService],
      },
   ],
   bootstrap: [AppComponent],
})
export class AppModule {}
